import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  RefObject,
} from 'react'

type Corner =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'

interface OverlayItem {
  element: React.ReactElement
  hookRef?: RefObject<HTMLElement> | HTMLElement | null
  corner: Corner
}

interface Position {
  position: 'fixed'
  top: string
  left: string
  transform?: string
}

interface GlobalOverlayContextType {
  addOverlay: (
    key: string,
    element: React.ReactElement,
    hookRef?: RefObject<HTMLElement> | HTMLElement | null,
    corner?: Corner,
  ) => void
  removeOverlay: (key: string) => void
  overlayItems: Record<string, OverlayItem>
  positions: Record<string, Position>
}

interface GlobalOverlayProviderProps {
  children: ReactNode
}

const GlobalOverlayContext = createContext<
  GlobalOverlayContextType | undefined
>(undefined)

export function GlobalOverlayProvider({
  children,
}: GlobalOverlayProviderProps) {
  const [overlayItems, setOverlayItems] = useState<Record<string, OverlayItem>>(
    {},
  )
  const [positions, setPositions] = useState<Record<string, Position>>({})

  // Add or update a JSX element in the overlay
  // If an overlay with the same key already exists, it will be replaced
  const addOverlay = useCallback(
    (
      key: string,
      element: React.ReactElement,
      hookRef: RefObject<HTMLElement> | HTMLElement | null = null,
      corner: Corner = 'top-left',
    ) => {
      setOverlayItems((prev) => ({
        ...prev,
        [key]: { element, hookRef, corner },
      }))
    },
    [],
  )

  // Remove an overlay by key
  const removeOverlay = useCallback((key: string) => {
    setOverlayItems((prev) => {
      const newItems = { ...prev }

      delete newItems[key]

      return newItems
    })
  }, [])

  // Update positions of all overlay items
  useEffect(() => {
    const updatePositions = () => {
      const newPositions: Record<string, Position> = {}

      Object.keys(overlayItems).forEach((key) => {
        const { hookRef, corner } = overlayItems[key]

        if (!hookRef) {
          // Center in window if no hook provided
          newPositions[key] = {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }

          return
        }

        // Handle both RefObject and direct HTMLElement
        const element = 'current' in hookRef ? hookRef.current : hookRef

        if (!element) {
          return
        }

        const rect = element.getBoundingClientRect()

        // Get overlay element dimensions for proper positioning
        const overlayElement = document.querySelector(
          `[data-overlay-key="${key}"]`,
        )
        const overlayRect = overlayElement
          ? overlayElement.getBoundingClientRect()
          : { width: 0, height: 0 }

        switch (corner) {
          case 'top-left':
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.top}px`,
              left: `${rect.left}px`,
            }
            break
          case 'top-right':
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.top}px`,
              left: `${rect.right}px`,
            }
            break
          case 'bottom-left':
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.bottom}px`,
              left: `${rect.left}px`,
            }
            break
          case 'bottom-right':
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.bottom}px`,
              left: `${rect.right}px`,
            }
            break
          case 'top':
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.top - overlayRect.height - 10}px`,
              left: `${rect.left + rect.width / 2}px`,
              transform: 'translateX(-50%)',
            }
            break
          case 'bottom':
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.bottom + 10}px`,
              left: `${rect.left + rect.width / 2}px`,
              transform: 'translateX(-50%)',
            }
            break
          case 'left':
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.top + rect.height / 2}px`,
              left: `${rect.left - overlayRect.width - 10}px`,
              transform: 'translateY(-50%)',
            }
            break
          case 'right':
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.top + rect.height / 2}px`,
              left: `${rect.right + 10}px`,
              transform: 'translateY(-50%)',
            }
            break
          default:
            newPositions[key] = {
              position: 'fixed',
              top: `${rect.top}px`,
              left: `${rect.left}px`,
            }
        }
      })

      setPositions(newPositions)
    }

    // Update positions immediately
    updatePositions()

    // Don't start animation frame if there are no items
    if (Object.keys(overlayItems).length === 0) {
      return
    } // Set up animation frame loop for continuous updates

    let animationFrameId: number

    const updateLoop = () => {
      updatePositions()
      animationFrameId = requestAnimationFrame(updateLoop)
    }

    animationFrameId = requestAnimationFrame(updateLoop)

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [overlayItems])

  const value: GlobalOverlayContextType = {
    addOverlay,
    removeOverlay,
    overlayItems,
    positions,
  }

  return (
    <GlobalOverlayContext.Provider value={value}>
      {children}
      {/* Render all overlay items */}
      {Object.keys(overlayItems).map((key) => {
        const position = positions[key]
        const { element } = overlayItems[key]

        if (!position || !element) {
          return null
        }

        return (
          <div
            key={key}
            data-overlay-key={key}
            style={{
              ...position,
              pointerEvents: 'none',
            }}
          >
            {element}
          </div>
        )
      })}
    </GlobalOverlayContext.Provider>
  )
}

export function useGlobalOverlay(): GlobalOverlayContextType {
  const context = useContext(GlobalOverlayContext)

  if (context === undefined) {
    throw new Error(
      'useGlobalOverlay must be used within a GlobalOverlayProvider',
    )
  }

  return context
}

export default GlobalOverlayContext
